import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ChevronRight, Building2, TrendingUp, Briefcase, LineChart, CheckCircle, Filter, Target, Users, Clock, Landmark } from 'lucide-react';
export const InvestmentPage = () => {
  const investmentAreas = [{
    title: 'Private Equity',
    description: 'We partner with established businesses seeking growth capital and operational expertise. Our private equity investments focus on middle-market companies with proven business models and opportunities for operational improvements and strategic expansion.',
    icon: <Building2 size={48} className="text-[#38b2ac]" data-id="element-462" />,
    details: ['Investment Size: $10-50 million', 'Target Ownership: Majority or significant minority', 'Industries: Business services, healthcare, industrial, consumer, and technology-enabled services', 'Company Profile: Established businesses with $3-15 million in EBITDA and strong growth potential'],
    approach: 'Our private equity strategy emphasizes operational improvements, strategic acquisitions, and organic growth initiatives. We work closely with management teams to optimize operations, enhance go-to-market strategies, and create sustainable competitive advantages.'
  }, {
    title: 'Venture Capital',
    description: 'Our venture investments target early and growth-stage companies with disruptive technologies and scalable business models. We provide not only capital but also strategic guidance, industry connections, and operational support to accelerate growth.',
    icon: <TrendingUp size={48} className="text-[#38b2ac]" data-id="element-463" />,
    details: ['Investment Size: $1-10 million', 'Investment Stage: Series A through Series C', 'Industries: Enterprise software, fintech, healthtech, and sustainable technologies', 'Company Profile: Technology-driven businesses with validated product-market fit and clear path to profitability'],
    approach: 'Our venture capital approach balances growth potential with disciplined metrics. We help portfolio companies refine their business models, accelerate customer acquisition, build strong executive teams, and prepare for future funding rounds or strategic exits.'
  }, {
    title: 'Business Acquisitions',
    description: 'We pursue selective acquisitions of established businesses with strong cash flows and defensible market positions. Our approach emphasizes preserving company legacy while implementing strategic improvements to drive long-term value creation.',
    icon: <Briefcase size={48} className="text-[#38b2ac]" data-id="element-464" />,
    details: ['Transaction Types: Management buyouts, owner recapitalizations, succession plans', 'Target Ownership: 100% ownership preferred', 'Industries: Essential services, manufacturing, distribution, and B2B services', 'Company Profile: Established businesses with $2-8 million in EBITDA and consistent cash flows'],
    approach: 'Our acquisition strategy focuses on businesses with strong fundamentals that can benefit from professional management, technology enhancement, and strategic direction. We maintain respect for company heritage while implementing improvements to drive sustainable growth.'
  }, {
    title: 'Real Asset Investments',
    description: 'Our real asset strategy focuses on investments in infrastructure, real estate, and natural resources that provide stable cash flows and inflation protection. We seek opportunities with strong underlying fundamentals and potential for value appreciation.',
    icon: <LineChart size={48} className="text-[#38b2ac]" data-id="element-465" />,
    details: ['Investment Size: $5-30 million', 'Asset Types: Commercial real estate, infrastructure projects, renewable energy', 'Target Returns: 8-12% annual yield with capital appreciation potential', 'Investment Horizon: 5-10 years'],
    approach: 'Our real assets approach emphasizes capital preservation, reliable income generation, and selective value-add opportunities. We focus on assets in growing markets with strong fundamentals and opportunities for operational improvements or development.'
  }];
  const investmentProcess = [{
    stage: 'Sourcing & Screening',
    description: 'We identify potential investments through our extensive network of industry contacts, intermediaries, and proprietary outreach. Initial screening evaluates business fundamentals, market dynamics, and alignment with our investment criteria.',
    icon: <Filter className="h-8 w-8 text-[#38b2ac]" data-id="element-466" />
  }, {
    stage: 'Due Diligence',
    description: 'Our rigorous due diligence process examines all aspects of the business including financial performance, operational capabilities, market position, competitive landscape, management team, and growth opportunities.',
    icon: <CheckCircle className="h-8 w-8 text-[#38b2ac]" data-id="element-467" />
  }, {
    stage: 'Value Creation Planning',
    description: 'We develop a comprehensive value creation plan in partnership with management, identifying specific initiatives to drive growth, improve operations, and enhance strategic positioning.',
    icon: <Target className="h-8 w-8 text-[#38b2ac]" data-id="element-468" />
  }, {
    stage: 'Active Partnership',
    description: 'Post-investment, we work closely with management teams to execute the value creation plan, providing strategic guidance, operational expertise, and access to our network of industry experts.',
    icon: <Users className="h-8 w-8 text-[#38b2ac]" data-id="element-469" />
  }, {
    stage: 'Responsible Exit',
    description: 'We plan for exits that maximize value for all stakeholders while ensuring the long-term sustainability of the business. Our approach considers strategic buyers, financial sponsors, or public markets depending on the specific situation.',
    icon: <Clock className="h-8 w-8 text-[#38b2ac]" data-id="element-470" />
  }];
  const featuredInvestments = [{
    name: 'MediTech Solutions',
    category: 'Private Equity',
    description: 'A leading provider of healthcare technology solutions that streamline clinical workflows and improve patient outcomes. Our investment supported product development and market expansion, resulting in 3x revenue growth over a four-year period.',
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    name: 'GreenEnergy Systems',
    category: 'Real Asset Investments',
    description: 'A renewable energy infrastructure company specializing in solar and wind projects. Our investment financed the development of multiple utility-scale installations, generating stable cash flows and contributing to the clean energy transition.',
    image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    name: 'DataStream Analytics',
    category: 'Venture Capital',
    description: 'An AI-powered data analytics platform that helps enterprises extract actionable insights from complex datasets. Our early-stage investment and strategic guidance helped the company achieve product-market fit and secure enterprise clients.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }];
  return <div className="font-sans text-gray-800 bg-white" data-id="element-471">
      <Header data-id="element-472" />
      <main data-id="element-473">
        {/* Hero Section */}
        <section className="relative bg-[#0a2351] text-white pt-20" data-id="element-474">
          <div className="absolute inset-0 z-0 opacity-20" data-id="element-475">
            <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2015&q=80" alt="Investment strategy" className="w-full h-full object-cover" data-id="element-476" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10" data-id="element-477">
            <div className="max-w-3xl" data-id="element-478">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-479">
                Investment Strategies
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-2xl" data-id="element-480">
                Strategic capital deployment across diverse investment
                opportunities
              </p>
              <nav className="flex" aria-label="Breadcrumb" data-id="element-481">
                <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-482">
                  <li className="inline-flex items-center" data-id="element-483">
                    <a href="/" className="text-gray-300 hover:text-white" data-id="element-484">
                      Home
                    </a>
                  </li>
                  <li data-id="element-485">
                    <div className="flex items-center" data-id="element-486">
                      <ChevronRight className="w-4 h-4 text-gray-300" data-id="element-487" />
                      <span className="ml-1 text-white md:ml-2" data-id="element-488">
                        Investments
                      </span>
                    </div>
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          <div className="h-16 bg-gradient-to-b from-[#0a2351] to-white opacity-20" data-id="element-489"></div>
        </section>
        {/* Investment Philosophy Section */}
        <section className="py-20 bg-white" data-id="element-490">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-491">
            <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-492">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#0a2351]" data-id="element-493">
                Our Investment Philosophy
              </h2>
              <div className="w-20 h-1 bg-[#38b2ac] mx-auto mb-8" data-id="element-494"></div>
            </div>
            <div className="max-w-4xl mx-auto" data-id="element-495">
              <div className="text-center mb-12" data-id="element-496">
                <p className="text-2xl leading-relaxed text-gray-700 italic" data-id="element-497">
                  "We believe that exceptional returns come from partnering with
                  exceptional businesses and providing them with the capital,
                  expertise, and support they need to thrive."
                </p>
              </div>
              <div className="bg-gray-50 p-8 rounded-lg shadow-sm" data-id="element-498">
                <p className="text-lg mb-6 leading-relaxed" data-id="element-499">
                  At Sapis Capital, our investment approach is guided by a
                  commitment to creating sustainable value through disciplined
                  capital allocation and active partnership. We focus on
                  opportunities where our capital and expertise can unlock
                  significant growth and operational improvements.
                </p>
                <p className="text-lg mb-6 leading-relaxed" data-id="element-500">
                  We believe in the power of alignment—ensuring that our
                  interests are fully aligned with those of our portfolio
                  companies, management teams, and co-investors. This alignment
                  forms the foundation for productive partnerships and
                  successful outcomes.
                </p>
                <p className="text-lg leading-relaxed" data-id="element-501">
                  Our investment decisions are driven by thorough analysis,
                  rigorous due diligence, and a deep understanding of industry
                  dynamics. We take a long-term view, focusing on building
                  sustainable businesses rather than pursuing short-term
                  financial engineering.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Investment Areas Section */}
        <section className="py-20 bg-gray-50" data-id="element-502">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-503">
            <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-504">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#0a2351]" data-id="element-505">
                Investment Areas
              </h2>
              <div className="w-20 h-1 bg-[#38b2ac] mx-auto mb-8" data-id="element-506"></div>
              <p className="text-xl text-gray-600" data-id="element-507">
                Strategic capital deployment across complementary investment
                strategies
              </p>
            </div>
            {investmentAreas.map((area, index) => <div key={index} className="mb-20 last:mb-0" data-id="element-508">
                <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-509">
                  <div className="md:flex" data-id="element-510">
                    <div className="md:w-1/3 bg-[#0a2351] text-white p-8 flex flex-col justify-center" data-id="element-511">
                      <div className="mb-6 flex justify-center md:justify-start" data-id="element-512">
                        {area.icon}
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-513">
                        {area.title}
                      </h3>
                      <p className="text-gray-100" data-id="element-514">{area.description}</p>
                    </div>
                    <div className="md:w-2/3 p-8" data-id="element-515">
                      <div className="mb-8" data-id="element-516">
                        <h4 className="text-xl font-semibold mb-4 text-[#0a2351]" data-id="element-517">
                          Investment Criteria
                        </h4>
                        <ul className="space-y-3" data-id="element-518">
                          {area.details.map((detail, i) => <li key={i} className="flex items-start" data-id="element-519">
                              <CheckCircle className="h-5 w-5 text-[#38b2ac] mr-3 mt-0.5 flex-shrink-0" data-id="element-520" />
                              <span className="text-gray-700" data-id="element-521">{detail}</span>
                            </li>)}
                        </ul>
                      </div>
                      <div data-id="element-522">
                        <h4 className="text-xl font-semibold mb-4 text-[#0a2351]" data-id="element-523">
                          Our Approach
                        </h4>
                        <p className="text-gray-700" data-id="element-524">{area.approach}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </section>
        {/* Investment Process Section */}
        <section className="py-20 bg-white" data-id="element-525">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-526">
            <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-527">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#0a2351]" data-id="element-528">
                Investment Process
              </h2>
              <div className="w-20 h-1 bg-[#38b2ac] mx-auto mb-8" data-id="element-529"></div>
              <p className="text-xl text-gray-600" data-id="element-530">
                Our systematic approach to evaluating and managing investments
              </p>
            </div>
            <div className="max-w-5xl mx-auto" data-id="element-531">
              {investmentProcess.map((step, index) => <div key={index} className={`flex flex-col md:flex-row items-start mb-12 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-532">
                  <div className={`md:w-1/4 mb-6 md:mb-0 ${index % 2 !== 0 ? 'md:pl-12' : 'md:pr-12'} flex justify-center md:justify-${index % 2 !== 0 ? 'end' : 'start'}`} data-id="element-533">
                    <div className="w-16 h-16 bg-[#0a2351] rounded-full flex items-center justify-center" data-id="element-534">
                      {step.icon}
                    </div>
                  </div>
                  <div className={`md:w-3/4 ${index % 2 !== 0 ? 'md:text-right' : ''}`} data-id="element-535">
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm" data-id="element-536">
                      <h3 className="text-xl font-semibold mb-3 text-[#0a2351]" data-id="element-537">
                        {index + 1}. {step.stage}
                      </h3>
                      <p className="text-gray-700" data-id="element-538">{step.description}</p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </section>
        {/* Investment Criteria Section */}
        <section className="py-20 bg-gray-50" data-id="element-539">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-540">
            <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-541">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#0a2351]" data-id="element-542">
                Investment Criteria
              </h2>
              <div className="w-20 h-1 bg-[#38b2ac] mx-auto mb-8" data-id="element-543"></div>
              <p className="text-xl text-gray-600" data-id="element-544">
                Key factors we evaluate when considering potential investments
              </p>
            </div>
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-545">
              <div className="bg-white p-8 rounded-lg shadow-sm" data-id="element-546">
                <div className="bg-[#0a2351] w-12 h-12 rounded-full flex items-center justify-center mb-6" data-id="element-547">
                  <Users className="h-6 w-6 text-white" data-id="element-548" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-[#0a2351]" data-id="element-549">
                  Strong Management
                </h3>
                <p className="text-gray-700" data-id="element-550">
                  We seek to partner with talented, ethical management teams
                  with a clear vision, strong execution capabilities, and
                  alignment with our values and objectives.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm" data-id="element-551">
                <div className="bg-[#0a2351] w-12 h-12 rounded-full flex items-center justify-center mb-6" data-id="element-552">
                  <Target className="h-6 w-6 text-white" data-id="element-553" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-[#0a2351]" data-id="element-554">
                  Market Position
                </h3>
                <p className="text-gray-700" data-id="element-555">
                  We look for businesses with defensible market positions,
                  differentiated offerings, and opportunities to build or
                  enhance competitive advantages.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm" data-id="element-556">
                <div className="bg-[#0a2351] w-12 h-12 rounded-full flex items-center justify-center mb-6" data-id="element-557">
                  <TrendingUp className="h-6 w-6 text-white" data-id="element-558" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-[#0a2351]" data-id="element-559">
                  Growth Potential
                </h3>
                <p className="text-gray-700" data-id="element-560">
                  We target opportunities with clear avenues for organic growth,
                  strategic expansions, or acquisition strategies that can drive
                  substantial value creation.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm" data-id="element-561">
                <div className="bg-[#0a2351] w-12 h-12 rounded-full flex items-center justify-center mb-6" data-id="element-562">
                  <Building2 className="h-6 w-6 text-white" data-id="element-563" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-[#0a2351]" data-id="element-564">
                  Industry Dynamics
                </h3>
                <p className="text-gray-700" data-id="element-565">
                  We focus on industries with favorable long-term trends,
                  limited cyclicality, and structural characteristics that
                  support sustainable profitability.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm" data-id="element-566">
                <div className="bg-[#0a2351] w-12 h-12 rounded-full flex items-center justify-center mb-6" data-id="element-567">
                  <Landmark className="h-6 w-6 text-white" data-id="element-568" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-[#0a2351]" data-id="element-569">
                  Financial Profile
                </h3>
                <p className="text-gray-700" data-id="element-570">
                  We evaluate businesses with strong financial foundations,
                  including healthy cash flows, manageable debt levels, and
                  opportunities for margin expansion.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm" data-id="element-571">
                <div className="bg-[#0a2351] w-12 h-12 rounded-full flex items-center justify-center mb-6" data-id="element-572">
                  <Clock className="h-6 w-6 text-white" data-id="element-573" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-[#0a2351]" data-id="element-574">
                  Value Creation Potential
                </h3>
                <p className="text-gray-700" data-id="element-575">
                  We assess our ability to contribute meaningfully to a
                  company's growth and operational improvements beyond just
                  providing capital.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Featured Investments Section */}
        <section className="py-20 bg-white" data-id="element-576">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-577">
            <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-578">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#0a2351]" data-id="element-579">
                Featured Investments
              </h2>
              <div className="w-20 h-1 bg-[#38b2ac] mx-auto mb-8" data-id="element-580"></div>
              <p className="text-xl text-gray-600" data-id="element-581">
                Selected case studies from our investment portfolio
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-582">
              {featuredInvestments.map((investment, index) => <div key={index} className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-583">
                  <div className="h-48 overflow-hidden" data-id="element-584">
                    <img src={investment.image} alt={investment.name} className="w-full h-full object-cover" data-id="element-585" />
                  </div>
                  <div className="p-6" data-id="element-586">
                    <div className="inline-block px-3 py-1 bg-[#0a2351] bg-opacity-10 text-[#0a2351] text-sm font-medium rounded-full mb-4" data-id="element-587">
                      {investment.category}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-[#0a2351]" data-id="element-588">
                      {investment.name}
                    </h3>
                    <p className="text-gray-600" data-id="element-589">{investment.description}</p>
                  </div>
                </div>)}
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="py-16 bg-[#0a2351]" data-id="element-590">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-591">
            <div className="max-w-4xl mx-auto text-center" data-id="element-592">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-593">
                Interested in Working with Us?
              </h2>
              <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto" data-id="element-594">
                Whether you're seeking investment for your business or looking
                to partner with us as an investor, we'd love to start a
                conversation.
              </p>
              <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-595">
                <a href="#contact" className="inline-block bg-[#38b2ac] hover:bg-[#319795] text-white font-medium py-3 px-8 rounded-md transition-colors duration-300" data-id="element-596">
                  Contact Our Team
                </a>
                <a href="/about-us" className="inline-block bg-transparent border-2 border-white text-white font-medium py-3 px-8 rounded-md hover:bg-white hover:text-[#0a2351] transition-colors duration-300" data-id="element-597">
                  Learn About Our Team
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-598" />
    </div>;
};